import { GenericError, useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { authExchange } from "@urql/exchange-auth";
import { RateLimiter } from "limiter";
import { ComponentProps, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  createClient,
  errorExchange,
  fetchExchange,
  getOperationName,
  mapExchange,
  Provider,
  subscriptionExchange,
} from "urql";

import { getNextFromPath } from "@/hooks/useNextParam";

import { fetchWithRetry } from "../../utils/fetch-retry";
import { createCacheExchange } from "./cache";
import { GraphqlWsClientContext } from "./GraphqlWsContext";
import { keyingConfig } from "./urqlCacheKeys";
import { optimisticConfig } from "./urqlCacheOptimistic";
import { resolverConfig } from "./urqlCacheResolvers";
import { updatesConfig } from "./urqlCacheUpdates";
// fetch only rejects on network errors, not http errors
// https://github.com/apollographql/apollo-feature-requests/issues/153

const rateLimiterMinute = new RateLimiter({
  tokensPerInterval: 500,
  interval: "minute",
  fireImmediately: true,
});

const rateLimiterSecond = new RateLimiter({
  tokensPerInterval: 30,
  interval: "second",
  fireImmediately: true,
});

const customFetch: typeof fetch = async (url, options) => {
  const remainingMinuteTokens = await rateLimiterMinute.removeTokens(1);
  const remainingSecondTokens = await rateLimiterSecond.removeTokens(1);

  if (remainingMinuteTokens < 0 || remainingSecondTokens < 0) {
    throw new Error("Rate limit exceeded");
  }

  let urlString: string;
  if (typeof url === "string") {
    urlString = url;
  } else if (url instanceof Request) {
    urlString = url.url;
  } else {
    urlString = url.toString();
  }
  const response = await fetchWithRetry(urlString, options);

  if (response.ok) {
    return response;
  }

  const responseMessage = await response.text();
  return Promise.reject(new Error(responseMessage));
};

const warn = console.error.bind(console);
type UrqlProviderProps = Omit<ComponentProps<typeof Provider>, "value">;

const isAuth0InvalidStateError = (error: unknown): boolean =>
  error instanceof GenericError && error.error_description === "Invalid state";

export default function UrqlProvider(props: UrqlProviderProps) {
  const navigate = useNavigate();
  const { isLoading, isAuthenticated, error, getAccessTokenSilently } =
    useAuth0();

  const { client: wsClient } = useContext(GraphqlWsClientContext);

  const client = useMemo(
    () =>
      createClient({
        url: new URL(__LAYER_GRAPHQL_URI__, window.location.origin).toString(),
        preferGetMethod: false,
        exchanges: [
          // Note: the order of exchanges is important, from bottom to top
          createCacheExchange({
            keys: keyingConfig,
            resolvers: resolverConfig,
            updates: updatesConfig,
            optimistic: optimisticConfig,
          }),
          errorExchange({
            onError(error, operation) {
              const operationName =
                // operation.query is DocumentNode | TypedDocumentNode | PersistedDocument;
                // getOperationName only accepts DocumentNode. This app never uses persisted
                // documents, so narrowing to the accepted parameter type is safe.
                getOperationName(
                  operation.query as Parameters<typeof getOperationName>[0],
                ) ?? "unknown";

              if (error.graphQLErrors.length > 0) {
                error.graphQLErrors.forEach((graphQLError) => {
                  warn("GraphQLError:", graphQLError.message);
                });
                Sentry.captureException(error, {
                  tags: {
                    graphql_operation: operationName,
                  },
                });
              }

              if (error.networkError) {
                warn("NetworkError:", error.networkError.message);
                Sentry.captureException(error.networkError, {
                  tags: {
                    error_type: "network_error",
                    graphql_operation: operationName,
                  },
                });
              }
            },
          }),
          authExchange(async () => {
            let token: string | undefined;
            try {
              token = isAuthenticated
                ? await getAccessTokenSilently()
                : undefined;
            } catch (error) {
              warn("Failed to refresh token:", error);
            }
            return {
              addAuthToOperation(operation) {
                const fetchOptions =
                  typeof operation.context.fetchOptions === "function"
                    ? operation.context.fetchOptions()
                    : operation.context.fetchOptions || {};
                return {
                  ...operation,
                  context: {
                    ...operation.context,
                    fetchOptions: {
                      ...fetchOptions,
                      headers: {
                        ...fetchOptions.headers,
                        authorization: `Bearer ${token}`,
                        "X-Layer-Client": "web-app",
                      },
                    },
                  },
                };
              },
              async refreshAuth() {
                try {
                  token = isAuthenticated
                    ? await getAccessTokenSilently()
                    : undefined;
                  wsClient?.terminate();
                } catch (error) {
                  warn("Failed to refresh token:", error);
                }
              },
              didAuthError(error) {
                return error.graphQLErrors.some(
                  (e) => e.extensions?.code === "FORBIDDEN",
                );
              },
            };
          }),
          mapExchange({
            onResult: (result) => {
              const hasInvalidJwtError = Object.values(result.data ?? {}).some(
                (data: any) => data?.code === "INVALID_JWT",
              );

              if (hasInvalidJwtError) {
                // Idea: convert INVALID_JWT to FORBIDDEN graphql error for didAuthError and always call getAccessTokenSilently with cache:off, but the UI does not react when we do that.
                navigate(
                  `/login?next=${getNextFromPath(window.location.pathname)}`,
                );
                return undefined;
              }

              return result;
            },
          }),
          fetchExchange,
          subscriptionExchange({
            forwardSubscription(request) {
              const input = { ...request, query: request.query || "" };
              return {
                subscribe(sink) {
                  if (!wsClient) {
                    return { unsubscribe: () => {} };
                  }
                  const unsubscribe = wsClient.subscribe(input, sink);
                  return { unsubscribe };
                },
              };
            },
          }),
        ],

        fetch: customFetch,
        fetchOptions: {},

        requestPolicy: "cache-and-network", // todo: @urql/exchange-request-policy
      }),
    [isAuthenticated, getAccessTokenSilently, wsClient, navigate],
  );

  if (error) {
    if (isAuth0InvalidStateError(error)) {
      navigate("/"); // this will cause auth0 to generate a new state
    } else {
      throw error; // don't worry about handling; caught by an enclosing error boundary
    }
  }
  if (isLoading) return null; // wait until Auth0 has finished loading; todo: suspense?

  return <Provider value={client} {...props} />;
}
